<template>
  <div class="md-layout text-center">
    <div class="md-layout-item md-size-50 md-medium-size-50 md-small-size-70 md-xsmall-size-100">
      <login-card header-color="blue">
        <h4 slot="title" class="title">Crie sua loja</h4>
        <h5 slot="subtitle" class="title">em menos de 5 minutos</h5>
        <p slot="description" class="description">{{ description() }}</p>

        <ValidationObserver ref="form" slot="inputs">
          <form @submit.prevent="validate">
            <ValidationProvider
              name="email"
              rules="required|email"
              v-slot="{ passed, failed }"
            >
              <md-field 
                :class="{
                  'md-error': failed,
                  'md-valid': passed,
                  'md-form-group': true
                }"
              >
                <md-icon>email</md-icon>
                <label>E-mail</label>
                <md-input v-model="email" autocomplete="email" type="email" @keydown="emailChange()"></md-input>

                <slide-y-down-transition>
                  <md-icon class="error" v-show="failed">close</md-icon>
                </slide-y-down-transition>
                <slide-y-down-transition>
                  <md-icon class="success" v-show="passed">done</md-icon>
                </slide-y-down-transition>
              </md-field>
            </ValidationProvider>

            <CollapseContent
              ref="passwdContent"
              :active="isLogin ? true : false"
              icon="keyboard_arrow_down"
              color-collapse="success"
            >
              <template slot="md-collapse-content">
                <ValidationProvider
                  name="password"
                  :rules="{ required: isLogin }"
                  v-slot="{ passed, failed }"
                >
                  <md-field 
                    :class="{
                      'md-error': isLogin && info.type == 'danger',
                      'md-error': failed,
                      'md-valid': passed,
                      'md-form-group': true
                    }"
                  >
                    <md-icon>lock</md-icon>
                    <label>Senha</label>
                    <md-input autocomplete="password" ref="password" v-model="password" type="password" @keydown="pwdChange()"></md-input>
                  </md-field>
                </ValidationProvider>

                <div class="pwd-reset">
                  <a v-if="!loader.loading" href="#" @click.prevent="pwdReset()">Esqueci minha senha</a>
                  <span v-if="loader.loading">Esqueci minha senha</span>
                </div>
              </template>
            </CollapseContent>

            <md-button type="submit" slot="footer" :disabled="loader.loading" class="md-success md-lg">Continuar</md-button>
          </form>
        </ValidationObserver>
      </login-card>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import { SlideYDownTransition } from 'vue2-transitions';
import { extend, validate } from 'vee-validate';
import { required, email } from 'vee-validate/dist/rules';
import { LoginCard } from '@/components';
import { CollapseContent } from '@/components';
import { VueReCaptcha } from 'vue-recaptcha-v3';

extend('required', required);
extend('email', email);

Vue.use(VueReCaptcha, { siteKey: '6LcU6BoaAAAAABFYp8YKpxs0pW_-kbrg30ArA4wb' });

export default {
  components: {
    LoginCard,
    SlideYDownTransition,
    CollapseContent
  },

  data() {
    return {
      session: this.$store.state.session,
      client: this.$store.state.client,
      loader: this.$store.state.loader,
      info: this.$store.state.info,
      isLogin: false,
      isProcessingRequest: false,
      email: null,
      password: null,
      mountedOnce: false // Flag para evitar múltiplas execuções
    };
  },

  mounted() {
    if (!this.$store.state.appLoaded) return; // Evita execução fora do momento de loading do App
    if (this.mountedOnce) return; // Evita execução duplicada
    this.mountedOnce = true;    
    this.$debug('Begin Mounted');

    this.$nextTick(() => {
      this.$gtm.trackEvent({
        event: 'Início',
        category: 'identification',
        action: 'view',
        noninteraction: false,
      });

      if (this.$functions.validateEmail(this.session.email)) {
        this.email = this.session.email;
        this.checkEmail();
      }
    });
  },

  methods: {
    async recaptcha() {
      await this.$recaptchaLoaded();
      this.$recaptchaInstance.hideBadge();
      const token = await this.$recaptcha(this.checkEmail());
      this.$debug('Recaptcha Token: ' + token);
    },

    description() {
      return this.isLogin ? `Informe sua senha, ${this.client.firstName}` : 'Informe seu e-mail';
    },

    emailChange() {
      if (this.email !== this.client.email) {
        this.isLogin = false;
        this.$store.commit('client', ['firstName', '']);
        this.$store.commit('loader', [false]);
        this.$functions.closeInfo();
      }
    },

    pwdChange() {
      this.$store.commit('loader', [false]);
      this.$functions.closeInfo();
    },

    validate() {
      this.$refs.form.validate().then(res => {
        this.$emit('on-validated', res);

        if (res) {
          if (this.isLogin) this.login();
          else this.recaptcha();
        }

        if (this.isLogin)
          this.$refs.password.$el.focus();
      });
    },

    async checkEmail() {
      this.$debug('Login => checkEmail');
      this.$store.commit('loader', [true, 'Aguarde', 'Verificando e-mail...']);

      const params = { email: this.email };

      this.$functions.get('checkEmail', params, 'login.php').then((response) => {
        if (!this.isLogin) this.$functions.resetData();

        this.$store.commit('client', ['email', this.email]);
        this.$store.commit('client', ['id', response.userid]);

        if (response.result === 'success') {
          this.$functions.showInfo(
            'Encontramos um cadastro com seu e-mail na iSET. Utilize sua senha para fazer login ou, caso não se lembre da senha, clique em "Esqueci minha senha".',
            'success'
          );
          this.$store.commit('client', ['firstName', response.firstname]);
          this.login();
        } else {
          this.next();
        }

        this.$store.commit('loader', [false]);
      });
    },

    login() {
      this.isLogin = true;
      this.$store.commit('loader', [true, 'Aguarde', 'Verificando seu login...']);

      this.$functions.doLogin(this.email, this.password, this.session.token).then((response) => {
        if (response)
          this.next();
        else {
          if (this.password) {
            const msg = 'E-mail ou senha incorretos. Caso precise, clique em "Esqueci minha senha" para receber um e-mail de redefinição de senha.';
            this.$functions.showInfo(msg, 'danger');
          }
        }
      });

      this.$store.commit('loader', [false]);
    },

    pwdReset() {
      this.$debug('Login => pwdReset');

      this.$store.commit('loader', [true, 'Aguarde', 'Redefinindo sua senha...']);

      const params = {
        clientId: this.client.id,
        email: this.client.email
      };

      this.$functions.post('resetPwd', params, 'login.php').then((response) => {
        if (response.result === 'success') {
          this.password = '';

          const msg = 'Uma nova senha foi gerada e enviada para seu e-mail. Caso você não o receba, por favor chame nosso atendimento via chat online.';
          this.$functions.showInfo(msg);
        } else {
          const msg = 'Não foi possível redefinir sua senha de acesso. Tente novamente e se o problema persistir chame nosso atendimento via chat online.';
          this.$functions.showInfo(msg, 'danger');
        }

        this.$store.commit('loader', [false]);
      });
    },

    next() {
      if (this.$store.state.session.email !== this.client.email) {
        this.$store.commit('session', ['email', this.client.email]);
        this.$functions.saveCookie('session');
      }

      const params = {
        id: this.$store.state.session.id,
        email: this.$store.state.client.email,
        utm_source: this.$store.state.session.utm_source,
        utm_medium: this.$store.state.session.utm_medium,
        utm_campaign: this.$store.state.session.utm_campaign,
        coupon: this.$store.state.session.coupon,
        affid: this.$store.state.session.affid,
        groupid: this.$store.state.session.gid,
        planid: this.$store.state.session.pid,
        clientid: this.$store.state.client.id,
        name: `${this.$store.state.client.firstName} ${this.$store.state.client.lastName}`,
        phone: this.$store.state.client.phone,
        newcustomer: this.$store.state.client.loggedIn ? 0 : 1,
        company: this.$store.state.business.company,
        persontype: this.$store.state.business.docType,
        city: this.$store.state.address.city,
        state: this.$store.state.address.state,
        country: this.$store.state.address.country,
        counter_begin: 1
      };

      this.$functions.addTrack(params);

      if (this.client.loggedIn)
        this.$router.push({ path: 'dominio' });
      else
        this.$router.push({ path: 'cadastro' });
    }
  }
};
</script>

<style lang="scss" scoped>
@import "../assets/scss/activation.scss";
.pwd-reset {
  font-size: $font-size-medium;
  text-align: right;
}
</style>
